import { useTheme } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { normalizeLanguageForI18n } from '@quadminds/shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpdateUser } from './useUpdateUser';
import { useUpdateUserSettings } from './useUpdateUserSettings';
import i18n from '../config/i18n';
import { OnboardingPreferences } from '../interfaces/onBoarding/on-boarding-pereferences.interface';

const ONBOARDING_LOCAL_KEY = 'onboarding-completed';

export const useOnboarding = () => {
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const { mutate: updateSettings, isPending: isUpdatingSettings } = useUpdateUserSettings();
    const { mutate: updateUser, isPending: isUpdatingUser } = useUpdateUser();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
    const [step, setStep] = useState(0);
    const { theme, setTheme } = useTheme();
    const location = useLocation();

    const currentOrgId = userInfo?.currentOrganization;
    const nextStep = () => setStep((prev) => prev + 1);

    const handleTermsChange = () => {
        setTermsAccepted(!termsAccepted);
    };

    const handleSkip = () => {
        // Complete onboarding with default values when skipped
        const defaultLanguage = normalizeLanguageForI18n(userInfo?.locale || 'es_ES.UTF-8');
        completeOnboarding({
            nickname: userInfo?.firstName || '',
            theme: theme === 'dark' ? 'dark' : 'light',
            language: defaultLanguage,
        });
    };

    const handleComplete = (preferences: OnboardingPreferences) => {
        completeOnboarding(preferences);
        setTheme(preferences.theme);
    };

    type MindySettings = {
        'mindy:onboarding_completed'?: boolean;
        'mindy:nickname'?: string;
        'mindy:theme'?: 'light' | 'dark';
        'mindy:terms'?: boolean;
        [key: string]: unknown;
    };

    const onboardingSettings =
        currentOrgId && userInfo?.settings
            ? (userInfo.settings[currentOrgId] as MindySettings)?.['mindy:onboarding_completed']
            : undefined;

    // Check localStorage for onboarding completion
    const localOnboardingCompleted = localStorage.getItem(ONBOARDING_LOCAL_KEY) === 'true';

    const shouldShowOnboarding =
        !onboardingSettings &&
        !localOnboardingCompleted &&
        location.pathname !== '/embedded' &&
        !userInfo?.originalUserData?.email?.includes('@quadminds.com');

    const handleAcceptedTerms = (noRenderModal: boolean) => {
        if (!userInfo?.id) return;

        updateSettings({
            userId: userInfo.id,
            settings: { 'mindy:terms': { accepted: true, date: new Date().toISOString() } },
            noRenderModal,
        });
    };

    const completeOnboarding = (preferences: OnboardingPreferences) => {
        if (!userInfo?.id) return;

        localStorage.setItem(ONBOARDING_LOCAL_KEY, 'true');

        // Normalize language to match supportedLngs using shared utility
        const normalizedLanguage = normalizeLanguageForI18n(preferences.language);

        // Optimistic update user language preference in i18next
        i18n.changeLanguage(normalizedLanguage);

        const settings = {
            'mindy:onboarding_completed': true,
            'mindy:nickname': preferences.nickname,
            'mindy:theme': preferences.theme,
            'mindy:terms': preferences.terms,
        };

        updateSettings({ userId: userInfo.id, settings });
        // Wait a little bit to not overwrite the previous toast so quickly
        setTimeout(() => {
            updateUser({ userId: userInfo.id, payload: { locale: preferences.language } });
        }, 1500);
    };

    const handleNextStepTerms = () => {
        if (termsAccepted) {
            handleAcceptedTerms(!termsAccepted);
        }
        nextStep();
    };

    const handleAcceptedPrivacyPolicy = () => {
        setPrivacyPolicyAccepted(!privacyPolicyAccepted);
    };

    return {
        shouldShowOnboarding,
        completeOnboarding,
        isLoading: isUpdatingSettings || isUpdatingUser,
        termsAccepted,
        handleTermsChange,
        handleNextStepTerms,
        nextStep,
        step,
        theme,
        setTheme,
        handleSkip,
        userInfo,
        handleComplete,
        privacyPolicyAccepted,
        handleAcceptedPrivacyPolicy,
        t,
    };
};
